<template>
  <a-card class="language-page" :title="copy.title" :bordered="false">
    <a-form-item :label="$t('language')">
      <a-select :value="$adminLocalePreference" :disabled="saving" style="width:240px;max-width:100%" @change="$setAdminLocale">
        <a-select-option value="system">{{ $t('followSystem') }}</a-select-option>
        <a-select-option value="zh-Hans">简体中文</a-select-option>
        <a-select-option value="zh-Hant">繁體中文</a-select-option>
        <a-select-option value="en">English</a-select-option>
      </a-select>
    </a-form-item>
    <div class="language-filters">
      <a-select :disabled="saving" :value="eventId" :placeholder="$t('eventTitle')" show-search :filter-option="false" @search="loadEvents" @change="selectEvent" style="width:320px;max-width:100%">
        <a-select-option v-for="event in events" :key="event.id" :value="event.id">{{ event.title }}</a-select-option>
      </a-select>
      <a-select v-if="eventId" :disabled="saving || loading" v-model="selection" @change="selectContent" style="width:240px;max-width:100%">
        <a-select-option value="event">{{ copy.event }}</a-select-option>
        <a-select-option v-for="category in categories" :key="category.id" :value="String(category.id)">{{ category.categoryName }}</a-select-option>
        <a-select-option v-for="stage in stages" :key="`stage:${stage.id}`" :value="`stage:${stage.id}`">{{ copy.stage }} · {{ stage.stageName }}</a-select-option>
        <a-select-option v-for="round in rounds" :key="`round:${round.id}`" :value="`round:${round.id}`">Race {{ round.raceNo || '—' }} · {{ round.roundName }}</a-select-option>
      </a-select>
    </div>
    <a-spin :spinning="loading">
      <a-form-model v-if="draft" :key="`${eventId}-${selection}`" :label-col="{span:4}" :wrapper-col="{span:18}">
        <single-source-content ref="content" :value="draft" :fields="fields" :kind="kind" :event-id="eventId" review-mode />
        <a-button type="primary" :loading="saving" @click="save">{{ copy.save }}</a-button>
      </a-form-model>
      <a-empty v-else :description="copy.choose" />
    </a-spin>
  </a-card>
</template>
<script>
import SingleSourceContent from '@/components/SingleSourceContent.vue'
import { getDragonEventList, getDragonEventDetail, saveDragonEvent, saveDragonCategory, getDragonStageConfigList, saveDragonStageConfig, getDragonRoundOptions, saveDragonRound } from '@/api'
import { createContentDraft, contentPayload, EVENT_CONTENT_FIELDS, CATEGORY_CONTENT_FIELDS, STAGE_CONTENT_FIELDS, ROUND_CONTENT_FIELDS } from '@/utils/single-source-content'
const messages = {
  'zh-Hans': { title: '语言管理', event: '赛事内容', stage: '阶段', choose: '选择需要维护的赛事', save: '保存', saved: '保存成功' },
  'zh-Hant': { title: '語言管理', event: '賽事內容', stage: '階段', choose: '選擇需要維護的賽事', save: '儲存', saved: '儲存成功' },
  en: { title: 'Languages', event: 'Event content', stage: 'Stage', choose: 'Select an event to maintain', save: 'Save', saved: 'Saved' }
}
export default {
  components: { SingleSourceContent },
  data: () => ({ events: [], eventId: undefined, event: null, categories: [], stages: [], rounds: [], selection: 'event', draft: null, loading: false, saving: false, serial: 0, searchSerial: 0 }),
  computed: {
    copy() { return messages[this.$adminLocale] || messages['zh-Hans'] },
    kind() { return this.selection === 'event' ? 'event' : this.selection.startsWith('stage:') ? 'stage' : this.selection.startsWith('round:') ? 'round' : 'category' },
    fields() { return { event: EVENT_CONTENT_FIELDS, category: CATEGORY_CONTENT_FIELDS, stage: STAGE_CONTENT_FIELDS, round: ROUND_CONTENT_FIELDS }[this.kind] }
  },
  created() { this.loadEvents() },
  methods: {
    async loadEvents(title = '') {
      const serial = ++this.searchSerial
      try {
        const res = await getDragonEventList({ page: 1, pageSize: 100, title })
        if (serial === this.searchSerial) this.events = res.data.list || []
      } catch (error) { this.$message.warning(error.message) }
    },
    async selectEvent(id) {
      const serial = ++this.serial
      this.eventId = id
      this.draft = null
      this.loading = true
      try {
        const [res, stageRes, roundRes] = await Promise.all([getDragonEventDetail({ id }), getDragonStageConfigList({ eventId: id }), getDragonRoundOptions({ eventId: id })])
        if (serial !== this.serial) return
        this.event = res.data.event
        this.categories = res.data.categories || []
        this.stages = stageRes.data.list || []
        this.rounds = roundRes.data || []
        this.selection = 'event'
        this.selectContent()
      } catch (error) { this.$message.warning(error.message) }
      finally { if (serial === this.serial) this.loading = false }
    },
    selectContent() {
      const record = this.kind === 'event' ? this.event : this.kind === 'stage'
        ? this.stages.find(item => `stage:${item.id}` === this.selection)
        : this.kind === 'round' ? this.rounds.find(item => `round:${item.id}` === this.selection)
        : this.categories.find(item => String(item.id) === this.selection)
      this.draft = record ? createContentDraft(record, this.fields, true) : null
    },
    async save() {
      if (this.saving || !this.draft) return
      this.saving = true
      try {
        await this.$refs.content.prepare()
        const result = await ({ event: saveDragonEvent, category: saveDragonCategory, stage: saveDragonStageConfig, round: saveDragonRound }[this.kind])(contentPayload(this.draft))
        if (this.kind === 'event') this.event = result.data
        else if (this.kind === 'stage') this.stages = this.stages.map(item => item.id === result.data.id ? result.data : item)
        else if (this.kind === 'round') this.rounds = this.rounds.map(item => item.id === result.data.id ? result.data : item)
        else this.categories = this.categories.map(item => item.id === result.data.id ? result.data : item)
        this.selectContent()
        this.$message.success(this.copy.saved)
      } catch (error) { this.$message.warning(error.message) }
      finally { this.saving = false }
    }
  }
}
</script>
<style scoped>
.language-filters { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 24px; }
</style>
